// src/services/siteStatusService.ts
// ─────────────────────────────────────────────────────────────────
// Site Status Service — Phase 2 (Mock)
//
// Phase 2 additions over Phase 1:
//   • getPushLogGrouped()     — groups push entries by session (90s window)
//   • getActivePushSession()  — most recent session for the delivery panel
//   • siteAddress lookup      — populated from MOCK_JOBS
//   • getJobsMap()            — job metadata cache for status cards
//
// At beta: replace function bodies with fetch('/api/site-status/...')
// ─────────────────────────────────────────────────────────────────

import { MOCK_ROSTER_NOTIFICATIONS, MOCK_WHS_NOTIFICATIONS } from '../utils/mockDataMobile'
import { getRosterPushLog }                                   from '../pages/Roster'
import type { RosterPushEntry }                               from '../pages/Roster'
import type { SiteStatusCard, WeatherSummary }               from '../types/mobileCrewTypes'
import { MOCK_JOBS }                                          from '../utils/mockData'

// ── Job metadata cache ─────────────────────────────────────────────
function buildJobsMap(): Record<string, { siteAddress: string; description: string; status: string }> {
  const map: Record<string, { siteAddress: string; description: string; status: string }> = {}
  MOCK_JOBS.forEach((j: any) => {
    map[j.id] = {
      siteAddress: j.siteAddress ?? '',
      description: j.description ?? '',
      status:      j.status ?? 'UNKNOWN',
    }
  })
  return map
}

// ── Mock weather by job ────────────────────────────────────────────
const MOCK_WEATHER: Record<string, WeatherSummary> = {
  j1: { condition: 'Partly cloudy',  tempC: 19, icon: '⛅', bomRef: 'IDQ60801', hasWarning: false },
  j2: { condition: 'Sunny',          tempC: 22, icon: '☀️', bomRef: 'IDQ60801', hasWarning: false },
  j3: { condition: 'Rain — Flood Watch', tempC: 16, icon: '🌧', bomRef: 'IDQ60801', hasWarning: true, warningType: 'Flood Watch — Gold Coast Hinterland' },
  j4: { condition: 'Mostly cloudy',  tempC: 18, icon: '🌥', bomRef: 'IDQ60801', hasWarning: false },
}

// ── Active permit and alert counts ────────────────────────────────
const MOCK_PERMIT_COUNTS: Record<string, number> = { j1: 0, j2: 1, j3: 0, j4: 2 }
const MOCK_ACTIVE_ALERTS: Record<string, number> = { j1: 1, j2: 0, j3: 0, j4: 0 }

// ── Build SiteStatusCard ───────────────────────────────────────────
function buildCard(jobId: string, jobsMap: ReturnType<typeof buildJobsMap>): SiteStatusCard | null {
  const jobRecords = MOCK_ROSTER_NOTIFICATIONS.filter(n => n.jobId === jobId)
  if (!jobRecords.length) return null

  const first    = jobRecords[0]
  const onSite   = jobRecords.filter(n => n.status === 'ON_SITE' || n.status === 'CONFIRMED').length
  const expected = jobRecords.length
  const pending  = jobRecords.filter(n => n.status === 'PENDING').length
  const recalled = jobRecords.some(n => n.status === 'RECALLED' || n.recalledAt !== null)
  const openWHS  = MOCK_WHS_NOTIFICATIONS.filter(n => n.jobId === jobId && n.status !== 'CLOSED').length
  const syncs    = jobRecords.map(n => n.confirmedAt).filter(Boolean) as string[]
  const lastSync = syncs.length ? syncs.sort().reverse()[0] : null
  const jobMeta  = jobsMap[jobId]

  // Derive shift status
  let currentShift: SiteStatusCard['currentShift'] = 'NOT_STARTED'
  if (recalled)         currentShift = 'RECALLED'
  else if (onSite > 0)  currentShift = 'DAY_ACTIVE'
  else if (jobRecords.some(n => n.status === 'COMPLETED')) currentShift = 'FINISHED'

  return {
    jobId,
    jobNumber:            first.jobNumber,
    jobName:              first.jobName,
    siteAddress:          jobMeta?.siteAddress ?? '',
    sitePostcode:         jobMeta?.siteAddress?.match(/QLD (\d{4})$/)?.[1] ?? '',
    crewOnSite:           onSite,
    crewExpected:         expected,
    crewPending:          pending,
    currentShift,
    shiftStartTime:       jobRecords.find(n => n.startTime)?.startTime ?? null,
    lastSyncAt:           lastSync,
    devicesOnSite:        onSite,
    activeWHSIncidents:   openWHS,
    openPermits:          MOCK_PERMIT_COUNTS[jobId] ?? 0,
    activeWHSOAlerts:     MOCK_ACTIVE_ALERTS[jobId] ?? 0,
    recallActive:         recalled,
    activeRecallId:       null,
    defectiveAssetsOnSite: 0,
    weather:              MOCK_WEATHER[jobId] ?? null,
  }
}

// ── Push log session grouping ──────────────────────────────────────

export interface PushLogSession {
  sessionKey:  string        // ISO of first entry's pushedAt
  pushedAt:    string
  pushedByName:string
  jobNumbers:  string[]
  shiftCount:  number
  confirmed:   number
  pending:     number
  rejected:    number
  entries:     RosterPushEntry[]
}

function groupPushEntries(entries: RosterPushEntry[]): PushLogSession[] {
  // Sort most-recent-first
  const sorted = [...entries].sort(
    (a, b) => new Date(b.pushedAt).getTime() - new Date(a.pushedAt).getTime()
  )

  const sessions: PushLogSession[] = []
  sorted.forEach(entry => {
    const match = sessions.find(s =>
      s.pushedByName === entry.pushedByName &&
      Math.abs(new Date(entry.pushedAt).getTime() - new Date(s.pushedAt).getTime()) < 90_000
    )

    if (match) {
      match.entries.push(entry)
      match.shiftCount++
      if (!match.jobNumbers.includes(entry.jobNumber)) match.jobNumbers.push(entry.jobNumber)
      if (entry.status === 'CONFIRMED') match.confirmed++
      else if (entry.status === 'REJECTED') match.rejected++
      else match.pending++
    } else {
      sessions.push({
        sessionKey:   entry.pushedAt,
        pushedAt:     entry.pushedAt,
        pushedByName: entry.pushedByName,
        jobNumbers:   [entry.jobNumber],
        shiftCount:   1,
        confirmed:    entry.status === 'CONFIRMED' ? 1 : 0,
        pending:      entry.status === 'PENDING'   ? 1 : 0,
        rejected:     entry.status === 'REJECTED'  ? 1 : 0,
        entries:      [entry],
      })
    }
  })
  return sessions
}

// ── Exported service functions ─────────────────────────────────────

/** Status summary for all active jobs. */
export async function getSiteStatusSummary(): Promise<SiteStatusCard[]> {
  // Phase 6: GET /api/site-status
  const jobsMap = buildJobsMap()
  const jobIds  = [...new Set(MOCK_ROSTER_NOTIFICATIONS.map(n => n.jobId))]
  return Promise.resolve(
    jobIds.map(id => buildCard(id, jobsMap)).filter(Boolean) as SiteStatusCard[]
  )
}

/** Status for a single job. */
export async function getSiteStatusByJob(jobId: string): Promise<SiteStatusCard | null> {
  // Phase 6: GET /api/site-status/:jobId
  return Promise.resolve(buildCard(jobId, buildJobsMap()))
}

/** Push log — raw entries from the Roster shared store. */
export async function getPushLog(jobId?: string): Promise<RosterPushEntry[]> {
  // Phase 6: GET /api/roster/push-log?jobId=...
  const log = getRosterPushLog()
  return Promise.resolve(jobId ? log.filter(e => e.jobId === jobId) : log)
}

/** Push log grouped by session (within 90 seconds = same push event). */
export async function getPushLogGrouped(jobId?: string): Promise<PushLogSession[]> {
  // Phase 6: GET /api/roster/push-log/grouped?jobId=...
  const log = getRosterPushLog()
  const filtered = jobId ? log.filter(e => e.jobId === jobId) : log
  return Promise.resolve(groupPushEntries(filtered))
}

/**
 * Most recent push session — used by the Live Crew push delivery panel.
 * Returns null if no pushes have been made this week.
 */
export async function getActivePushSession(): Promise<PushLogSession | null> {
  // Phase 6: GET /api/roster/push-log/latest-session
  const log = getRosterPushLog()
  if (!log.length) return null
  const sessions = groupPushEntries(log)
  return Promise.resolve(sessions[0] ?? null)
}

/** Weather for a job's site. Phase 6: real BOM API call per postcode. */
export async function getSiteWeather(jobId: string): Promise<WeatherSummary | null> {
  // Phase 6: GET /api/weather?postcode=...
  return Promise.resolve(MOCK_WEATHER[jobId] ?? null)
}
